import { lazy } from 'react';

// Lazy load all major components to improve initial page load
export const LazyAbout = lazy(() =>
  import('../components/About').then(module => ({ default: module.default }))
);

export const LazyChairman = lazy(() =>
  import('../components/Chairman').then(module => ({ default: module.default }))
);

export const LazyCapabilities = lazy(() =>
  import('../components/Capabilities').then(module => ({ default: module.default }))
);

export const LazyProducts = lazy(() =>
  import('../components/Products').then(module => ({ default: module.default }))
);

export const LazyProcessFlow = lazy(() =>
  import('../components/ProcessFlow').then(module => ({ default: module.default }))
);

export const LazyQuality = lazy(() =>
  import('../components/Quality').then(module => ({ default: module.default }))
);

export const LazySectors = lazy(() =>
  import('../components/Sectors').then(module => ({ default: module.default }))
);

export const LazyTestimonials = lazy(() =>
  import('../components/Testimonials').then(module => ({ default: module.default }))
);

export const LazyCTABanner = lazy(() =>
  import('../components/CTABanner').then(module => ({ default: module.default }))
);

export const LazyContact = lazy(() =>
  import('../components/Contact').then(module => ({ default: module.default }))
);

export const LazyFooter = lazy(() =>
  import('../components/Footer').then(module => ({ default: module.default }))
);

// Lazy loading with intersection observer for better performance
export class LazyComponentLoader {
  constructor() {
    this.observers = new Map();
    this.loadedComponents = new Set();
  }

  // Create intersection observer for component
  observeComponent(element, loadCallback, options = {}) {
    const defaultOptions = {
      threshold: 0.1,
      rootMargin: '100px 0px', // Start loading 100px before element enters viewport
      ...options
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loadedComponents.has(entry.target)) {
            loadCallback();
            this.loadedComponents.add(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      defaultOptions
    );

    if (element) {
      observer.observe(element);
      this.observers.set(element, observer);
    }

    return observer;
  }

  // Cleanup observers
  cleanup() {
    this.observers.forEach((observer, element) => {
      observer.unobserve(element);
    });
    this.observers.clear();
  }
}

// Global lazy loader instance
export const lazyLoader = new LazyComponentLoader();

// Preload critical components after initial page load
export const preloadCriticalComponents = () => {
  // Preload components that are likely to be viewed soon
  const preloadPromises = [
    import('../components/About'),
    import('../components/Chairman'),
    import('../components/Capabilities')
  ];

  // Preload in background without blocking
  Promise.all(preloadPromises).catch(err => {
    console.warn('Component preloading failed:', err);
  });
};

// Preload images and other assets
export const preloadAssets = (assets) => {
  return Promise.all(
    assets.map(asset => {
      return new Promise((resolve, reject) => {
        if (asset.type === 'image') {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = asset.src;
        } else if (asset.type === 'video') {
          const video = document.createElement('video');
          video.onloadedmetadata = resolve;
          video.onerror = reject;
          video.preload = 'metadata';
          video.src = asset.src;
        } else {
          resolve(); // Unknown type, just resolve
        }
      });
    })
  );
};

// Performance monitoring
export const performanceMonitor = {
  startTime: performance.now(),

  mark(name) {
    performance.mark(name);
  },

  measure(name, startMark, endMark) {
    try {
      performance.measure(name, startMark, endMark);
      const measure = performance.getEntriesByName(name)[0];
      console.log(`${name}: ${measure.duration.toFixed(2)}ms`);
      return measure.duration;
    } catch (error) {
      console.warn(`Performance measurement failed for ${name}:`, error);
    }
  },

  getLoadTime() {
    return performance.now() - this.startTime;
  }
};